import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import { useCallback, useEffect, useRef } from 'react';
import {
  useQuizResultActions,
  useQuizGameState,
  useQuizMetaContext,
  useQuizResults,
} from '@mentimeter/quiz';
import { TitleSection } from '@mentimeter/question-modules-shared';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import { GameStateEnums, type InitialGameStates } from '@mentimeter/quiz-types';
import { RevealAnswer } from '../../shared/components/quiz/RevealAnswer';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Lobby } from '../../shared/components/quiz/lobby';
import { GetReady } from '../../shared/components/quiz/get-ready';
import { Countdown } from '../../shared/components/quiz/countdown';
import { Error } from '../../shared/components/quiz/error';
import { Loading } from '../../shared/components/quiz/loading';
import { Results } from './results/Results';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type QuizSlideState = {
  started?: boolean;
  starting?: boolean;
};

export const LivePresentation = ({
  initialGameState,
  activeSession,
  correctAnswer,
}: {
  initialGameState: InitialGameStates;
  activeSession: number;
  correctAnswer: string | undefined;
}) => {
  const { useQuestion, useSlideState, useConfig } = usePresentationContext();
  const { readonly } = useConfig();
  const {
    countdown_to_vote: countdownToVote,
    image,
    time_based_scoring,
    question,
    question_styled,
    title_meta,
    question_description,
    id,
    layout,
  } = useQuestion();
  const { totalCount, currentIndex } = useQuizMetaContext();
  const { data = [], isPreviewResults } =
    useQuizResults<QuizPlayersScoreData>();
  const { markAnswer } = useQuizResultActions(id);
  const {
    gameState,
    timeOffset = 0,
    connectedPlayers,
    respondents,
    startQuiz: _startQuiz,
    createQuiz,
    everyoneHasVoted,
    connected,
    connectedWithDelay,
  } = useQuizGameState();
  const [{ started, starting }, setSlideState] =
    useSlideState<QuizSlideState>();

  const startQuiz = useCallback(() => {
    _startQuiz({ countdownToVote });
    setSlideState({ started: true, starting: false });
  }, [countdownToVote, setSlideState, _startQuiz]);

  /**
   * We need to reset our slide state whenever a presenter shows a quiz slide to be able
   * to restart a quiz for example.
   */
  useEffect(() => {
    setSlideState({ started: false, starting: false });
  }, [setSlideState]);

  useEffect(() => {
    if (starting && !started) {
      startQuiz();
    }
  }, [startQuiz, started, starting]);

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      createQuiz(sessionChanged ? GameStateEnums.LOBBY : initialGameState);
      createdRef.current = true;
      prevActiveSession.current = activeSession;
    }
  }, [createQuiz, initialGameState, activeSession, gameState]);

  const shouldShowLegacyImage = layout?.type === 'default';

  if (isPreviewResults) {
    return (
      <>
        <TitleSection
          title={question}
          styledTitle={question_styled}
          label={title_meta}
          fontSizeOffset={layout?.font_size_offset}
        />
        {data.length > 0 && (
          <RevealAnswer correctAnswer={correctAnswer}>
            <Results
              onMarkAnswer={markAnswer}
              results={data}
              isReadonly={readonly}
            />
          </RevealAnswer>
        )}
      </>
    );
  }

  switch (gameState?.gameState) {
    case GameStateEnums.LOBBY: {
      return (
        <CenteringContainer data-testid="gamestate-lobby">
          <Lobby
            connected={connectedWithDelay}
            total={totalCount}
            current={currentIndex + 1}
            players={connectedPlayers}
          />
        </CenteringContainer>
      );
    }
    case GameStateEnums.GET_READY: {
      return (
        <CenteringContainer>
          <GetReady timeBasedScoring={time_based_scoring} />
        </CenteringContainer>
      );
    }
    case GameStateEnums.COUNTDOWN: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={question_styled}
            label={title_meta}
            description={question_description}
            fontSizeOffset={layout?.font_size_offset}
          />
          <CenteringContainer>
            <Countdown
              type="open"
              respondents={respondents.length}
              everyoneHasVoted={everyoneHasVoted}
              imageUrl={
                image?.presets?.original?.url ?? image?.presets?.large?.url
              }
              timeOffset={timeOffset}
              startAt={gameState.gameStateData.startAt}
              endAt={gameState.gameStateData.endAt}
              shouldShowLegacyImage={shouldShowLegacyImage}
            />
          </CenteringContainer>
        </>
      );
    }
    case GameStateEnums.RESULT: {
      return (
        <>
          <TitleSection
            title={question}
            styledTitle={question_styled}
            label={title_meta}
            fontSizeOffset={layout?.font_size_offset}
          />
          <RevealAnswer correctAnswer={correctAnswer}>
            <Results
              onMarkAnswer={markAnswer}
              results={data}
              isReadonly={readonly}
            />
          </RevealAnswer>
        </>
      );
    }
    case GameStateEnums.ERROR: {
      return (
        <CenteringContainer>
          <Error errorCode={gameState.errorCode} />
        </CenteringContainer>
      );
    }
    default: {
      return (
        <CenteringContainer>
          <Loading
            onReload={() => {
              if (connected) {
                createQuiz(initialGameState);
              } else {
                window.location.reload();
              }
            }}
          />
        </CenteringContainer>
      );
    }
  }
};
